<template>
  <li
    v-if="shouldRenderMessage"
    :id="`message${data.id}`"
    :class="[alignBubble, { 'date-margin': showDate }]"
  >
    <div v-if="showDate" class="relative-date">{{ relativeDate }}</div>
    <div class="message-block">
      <div :class="wrapClass">
        <div v-if="isFailed" class="message-failed--alert">
          <woot-button
            v-tooltip.top-end="$t('CONVERSATION.TRY_AGAIN')"
            size="tiny"
            color-scheme="alert"
            variant="clear"
            icon="arrow-clockwise"
            @click="retrySendMessage"
          />
        </div>
        <div v-if="!showAvatar" class="mr-[5px]" style="display: flex !important; align-items: flex-end !important;">
          <woot-thumbnail
            :src="currentContact.thumbnail"
            :username="currentContact.name"
            size="35px"
          />
        </div>
        <div
          v-tooltip.top-start="messageToolTip"
          :class="bubbleClass"
          @contextmenu="openContextMenu($event)"
        >
          <bubble-mail-head
            :email-attributes="contentAttributes.email"
            :cc="emailHeadAttributes.cc"
            :bcc="emailHeadAttributes.bcc"
            :is-incoming="isIncoming"
          />
          <blockquote v-if="storyReply" class="story-reply-quote">
            <span>{{ $t('CONVERSATION.REPLIED_TO_STORY') }}</span>
            <bubble-image
              v-if="!hasImgStoryError && storyUrl"
              :url="storyUrl"
              @error="onStoryLoadError"
            />
            <bubble-video
              v-else-if="hasImgStoryError && storyUrl"
              :url="storyUrl"
            />
          </blockquote>
          <bubble-text
            v-if="data.content"
            :message="message"
            :is-email="isEmailContentType"
            :display-quoted-button="displayQuotedButton"
            :content-attributes="contentAttributes"
            :conversation-id="data.conversation_id"
          />
          <bubble-integration
            :message-id="data.id"
            :content-attributes="contentAttributes"
            :inbox-id="data.inbox_id"
          />
          <bubble-audio
            :message-id="data.id"
            :additional-attributes="additionalAttributes"
            :inbox-id="data.inbox_id"
          />
          <span
            v-if="isPending && hasAttachments"
            class="chat-bubble has-attachment agent"
          >
            {{ $t('CONVERSATION.UPLOADING_ATTACHMENTS') }}
          </span>
          <div v-if="!isPending && hasAttachments">
            <div v-for="attachment in attachments" :key="attachment.id">
              <bubble-image-audio-video
                v-if="isAttachmentImageVideoAudio(attachment.file_type)"
                :attachment="attachment"
                @error="onImageLoadError"
              />
              <bubble-location
                v-else-if="attachment.file_type === 'location'"
                :latitude="attachment.coordinates_lat"
                :longitude="attachment.coordinates_long"
                :name="attachment.fallback_title"
              />
              <bubble-contact
                v-else-if="attachment.file_type === 'contact'"
                :name="data.content"
                :phone-number="attachment.fallback_title"
              />
              <instagram-image-error-placeholder
                v-else-if="hasImageError && hasInstagramStory"
              />
              <bubble-file v-else :url="attachment.data_url" />
            </div>
          </div>
        </div>

        <spinner v-if="isPending" size="tiny" />
        <div
          v-if="showAvatar"
          v-tooltip.left="tooltipForSender"
          class="sender--info"
        >
          <woot-thumbnail
            :src="sender.thumbnail"
            :username="senderNameForAvatar"
            size="35px"
          />
          <a
            v-if="isATweet && isIncoming"
            class="sender--available-name"
            :href="twitterProfileLink"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            {{ sender.name }}
          </a>
        </div>
        
        <div
          v-if="shouldShowContextMenu && isIncoming"
          class="context-menu-wrap context-menu-wrap--incoming"
        >
          <context-menu
            v-if="isBubble && !isMessageDeleted"
            :context-menu-position="contextMenuPosition"
            :is-open="showContextMenu"
            :enabled-options="contextMenuEnabledOptions"
            :message="data"
            :campaign-name="campaignName"
            @open="openContextMenu"
            @close="closeContextMenu"
          />
        </div>
      </div>
      <bubble-actions
        :id="data.id"
        :sender="data.sender"
        :story-sender="storySender"
        :external-error="externalError"
        :story-id="`${storyId}`"
        :is-a-tweet="isATweet"
        :is-a-whatsapp-channel="isAWhatsAppChannel"
        :has-instagram-story="hasInstagramStory"
        :is-email="isEmailContentType"
        :is-private="data.private"
        :message-type="data.message_type"
        :message-status="status"
        :source-id="data.source_id"
        :inbox-id="data.inbox_id"
        :created-at="createdAt"
      />
    </div>

    <div
      v-if="shouldShowContextMenu && !isIncoming"
      class="context-menu-wrap"
    >
      <context-menu
        v-if="isBubble && !isMessageDeleted"
        :context-menu-position="contextMenuPosition"
        :is-open="showContextMenu"
        :enabled-options="contextMenuEnabledOptions"
        :message="data"
        :campaign-name="campaignName"
        @open="openContextMenu"
        @close="closeContextMenu"
      />
    </div>
  </li>
</template>
<script>
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import BubbleActions from './bubble/Actions';
import BubbleFile from './bubble/File';
import BubbleImage from './bubble/Image';
import BubbleVideo from './bubble/Video';
import BubbleImageAudioVideo from './bubble/ImageAudioVideo';
import BubbleIntegration from './bubble/Integration.vue';
import BubbleLocation from './bubble/Location';
import BubbleMailHead from './bubble/MailHead';
import BubbleText from './bubble/Text';
import BubbleContact from './bubble/Contact';
import Spinner from 'shared/components/Spinner.vue';
import ContextMenu from 'dashboard/modules/conversations/components/MessageContextMenu';
import instagramImageErrorPlaceholder from './instagramImageErrorPlaceholder.vue';
import alertMixin from 'shared/mixins/alertMixin';
import contentTypeMixin from 'shared/mixins/contentTypeMixin';
import { MESSAGE_TYPE, MESSAGE_STATUS } from 'shared/constants/messages';
import { generateBotMessageContent } from './helpers/botMessageContentHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import BubbleAudio from './bubble/Audio.vue';
import { mapGetters } from 'vuex';
export default {
  components: {
    BubbleActions,
    BubbleFile,
    BubbleImage,
    BubbleVideo,
    BubbleAudio,
    BubbleImageAudioVideo,
    BubbleIntegration,
    BubbleLocation,
    BubbleMailHead,
    BubbleText,
    BubbleContact,
    ContextMenu,
    Spinner,
    instagramImageErrorPlaceholder,
  },
  mixins: [alertMixin, messageFormatterMixin, contentTypeMixin],
  props: {
    data: {
      type: Object,
      required: true,
    },
    isATweet: {
      type: Boolean,
      default: false,
    },
    isAWhatsAppChannel: {
      type: Boolean,
      default: false,
    },
    hasInstagramStory: {
      type: Boolean,
      default: false,
    },
    isWebWidgetInbox: {
      type: Boolean,
      default: false,
    },
    showDate: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      showContextMenu: false,
      hasImageError: false,
      contextMenuPosition: {},
      showBackgroundHighlight: false,
      hasImgStoryError: false,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentChat: 'getSelectedChat',
    }),
    currentUserName() {
      return this.currentUser.available_name;
    },
    attachments() {
      const all = (this.data?.attachments || []).map(attachment => ({
        ...attachment,
        sender: this.data.sender || {},
        created_at: this.data.created_at || '',
      }));

      // Hide image attachments whose signed_id is already inline in the
      // body, so drag-dropped inline images don't render twice.
      const content = this.data?.content;
      if (!content) return all;
      return all.filter(att => {
        if (att.file_type !== 'image') return true;
        const signedId = this.extractActiveStorageSignedId(att.data_url);
        if (!signedId) return true;
        return !content.includes(signedId);
      });
    },
    shouldRenderMessage() {
      return (
        this.hasAttachments ||
        this.data.content ||
        this.isEmailContentType ||
        this.isAnIntegrationMessage
      );
    },
    emailMessageContent() {
      const {
        html_content: { full: fullHTMLContent } = {},
        text_content: { full: fullTextContent } = {},
      } = this.contentAttributes.email || {};
      return fullHTMLContent || fullTextContent || '';
    },
    displayQuotedButton() {
      if (this.emailMessageContent.includes('<blockquote')) {
        return true;
      }

      if (!this.isIncoming) {
        return false;
      }

      return false;
    },
    message() {
      // If the message is an email, emailMessageContent would be present
      // In that case, we would use letter package to render the email
      if (this.emailMessageContent && this.isIncoming) {
        return this.emailMessageContent;
      }

      const botMessageContent = generateBotMessageContent(
        this.contentType,
        this.contentAttributes,
        {
          noResponseText: this.$t('CONVERSATION.NO_RESPONSE'),
          flowResponseTitle: this.$t('CONVERSATION.FLOW_RESPONSE'),
          csat: {
            ratingTitle: this.$t('CONVERSATION.RATING_TITLE'),
            feedbackTitle: this.$t('CONVERSATION.FEEDBACK_TITLE'),
          },
        }
      );

      if (this.contentAttributes.flow_response) {
        return botMessageContent;
      }

      if (this.contentType === 'input_csat') {
        return this.$t('CONVERSATION.CSAT_REPLY_MESSAGE') + botMessageContent;
      }

      return (
        this.formatMessage(
          this.data.content,
          this.isATweet,
          this.data.private
        ) + botMessageContent
      );
    },
    contextMenuEnabledOptions() {
      const attrs = this.contentAttributes || {};
      const email = attrs.email || {};
      const hasEmailHeaders = !!(
        (email.from && email.from.length) ||
        (email.to && email.to.length) ||
        (attrs.to_emails && attrs.to_emails.length) ||
        (attrs.cc_emails && attrs.cc_emails.length)
      );

      return {
        copy: this.hasText,
        delete: false,
        cannedResponse: this.isOutgoing && this.hasText,
        replyAll: hasEmailHeaders && !this.data.private,
      };
    },
    contentAttributes() {
      return this.data.content_attributes || {};
    },
    isSmartfloInboundCall() {
      if (this.contentAttributes.smartflo_call_direction === 'inbound') {
        return true;
      }
      return (
        this.data.private &&
        (this.data.content || '').includes('**Incoming Call:**')
      );
    },
    externalError() {
      return this.contentAttributes.external_error || null;
    },
    sender() {
      return this.data.sender || {};
    },
    status() {
      return this.data.status;
    },
    storySender() {
      return this.contentAttributes.story_sender || null;
    },
    storyId() {
      return this.contentAttributes.story_id || null;
    },
    storyUrl() {
      return this.contentAttributes.story_url || null;
    },
    storyReply() {
      return this.storyUrl && this.hasInstagramStory;
    },
    contentType() {
      const {
        data: { content_type: contentType },
      } = this;
      return contentType;
    },
    isCampaignMessage() {
      return !!(this.additionalAttributes?.campaign_id);
    },
    campaignId() {
      return this.additionalAttributes?.campaign_id;
    },
    campaignName() {
      if (!this.campaignId) return null;
      // First check if campaign_name is stored directly in the message
      console.log("this.additionalAttributes", this.additionalAttributes);
      if (this.additionalAttributes?.campaign_name) {
        return this.additionalAttributes.campaign_name;
      }
      // Fallback to campaign ID if name is not stored
      return `Campaign #${this.campaignId}`;
    },
    twitterProfileLink() {
      const additionalAttributes = this.sender.additional_attributes || {};
      const { screen_name: screenName } = additionalAttributes;
      return `https://twitter.com/${screenName}`;
    },
    alignBubble() {
      const { message_type: messageType } = this.data;
      const isCentered = messageType === MESSAGE_TYPE.ACTIVITY;
      const isLeftAligned =
        messageType === MESSAGE_TYPE.INCOMING || this.isSmartfloInboundCall;
      const isRightAligned =
        (messageType === MESSAGE_TYPE.OUTGOING ||
          messageType === MESSAGE_TYPE.TEMPLATE) &&
        !this.isSmartfloInboundCall;
      return {
        center: isCentered,
        left: isLeftAligned,
        right: isRightAligned,
        'has-context-menu': this.showContextMenu,
        'has-tweet-menu': this.isATweet,
        'has-bg': this.showBackgroundHighlight,
      };
    },
    createdAt() {
      return this.contentAttributes.external_created_at || this.data.created_at;
    },
    isBubble() {
      return [0, 1, 3].includes(this.data.message_type);
    },
    additionalAttributes() {
      return this.data.additional_attributes || {};
    },
    isIncoming() {
      return (
        this.data.message_type === MESSAGE_TYPE.INCOMING ||
        this.isSmartfloInboundCall
      );
    },
    isOutgoing() {
      return (
        this.data.message_type === MESSAGE_TYPE.OUTGOING &&
        !this.isSmartfloInboundCall
      );
    },
    isTemplate() {
      return this.data.message_type === MESSAGE_TYPE.TEMPLATE;
    },
    isAnIntegrationMessage() {
      return this.contentType === 'integrations';
    },
    emailHeadAttributes() {
      return {
        email: this.contentAttributes.email,
        cc: this.contentAttributes.cc_emails,
        bcc: this.contentAttributes.bcc_emails,
      };
    },
    hasAttachments() {
      return !!(this.data.attachments && this.data.attachments.length > 0);
    },
    isMessageDeleted() {
      return this.contentAttributes.deleted;
    },
    hasText() {
      return !!this.data.content;
    },
    tooltipForSender() {
      const { message_type: messageType } = this.data;
      const showTooltip =
        messageType === MESSAGE_TYPE.OUTGOING ||
        messageType === MESSAGE_TYPE.TEMPLATE;
      return showTooltip
        ? {
            content: `${this.$t('CONVERSATION.SENT_BY')} ${this.senderNameForAvatar}`,
          }
        : false;
    },
    messageToolTip() {
      if (this.isMessageDeleted) {
        return false;
      }
      if (this.isFailed) {
        return this.externalError ? '' : this.$t(`CONVERSATION.SEND_FAILED`);
      }
      return false;
    },
    wrapClass() {
      return {
        wrap: this.isBubble,
        'activity-wrap': !this.isBubble,
        'is-pending': this.isPending,
        'is-failed': this.isFailed,
        'is-email': this.isEmailContentType,
      };
    },
    bubbleClass() {
      /*return {
        bubble: this.isBubble,
        'is-private': this.data.private,
        'is-image': this.hasMediaAttachment('image'),
        'is-video': this.hasMediaAttachment('video'),
        'is-text': this.hasText,
        // 'color-blue-text': this.hasText,
        'is-from-bot': this.isSentByBot,
        'is-failed': this.isFailed,
        'is-email': this.isEmailContentType,
      }; */
      const classes = {
        bubble: this.isBubble,
        'is-private': this.data.private,
        'is-image': this.hasMediaAttachment('image'),
        'is-video': this.hasMediaAttachment('video'),
        'is-text': this.hasText,
        'is-from-bot': this.isSentByBot && !this.data.private,
        'is-failed': this.isFailed,
        'is-email': this.isEmailContentType,
        'is-campaign': this.isCampaignMessage,
      };

      // Check if the classes object has exactly 'bubble' and 'is-text' classes
      const activeClasses = Object.keys(classes).filter(key => classes[key]);
      const hasExactBubbleTextClass =
        activeClasses.length === 2 &&
        activeClasses.includes('bubble') &&
        activeClasses.includes('is-text');

      // Conditionally add 'color-blue-text' class
      // if (hasExactBubbleTextClass) {
      //   classes['color-blue-text'] = true;
      // }

      return classes;
    },
    isPending() {
      return this.data.status === MESSAGE_STATUS.PROGRESS;
    },
    isFailed() {
      return this.data.status === MESSAGE_STATUS.FAILED;
    },
    isSentByBot() {
      if (this.isPending || this.isFailed) return false;
      return !this.sender.type || this.sender.type === 'agent_bot';
    },
    shouldShowContextMenu() {
      return !(this.isFailed || this.isPending);
    },
    errorMessage() {
      const { meta } = this.data;
      return meta ? meta.error : '';
    },
    showAvatar() {
      if (this.isSmartfloInboundCall) {
        return false;
      }
      if (this.isOutgoing || this.isTemplate) {
        return true;
      }
      return this.isATweet && this.isIncoming && this.sender;
    },
    senderNameForAvatar() {
      if (this.isOutgoing || this.isTemplate) {
        const { name = this.$t('CONVERSATION.BOT') } = this.sender || {};
        return name;
      }
      return '';
    },

    messageDate() {
      const timestamp =
        typeof this.createdAt === 'number'
          ? this.createdAt * 1000
          : this.createdAt;
      return new Date(timestamp);
    },

    shouldShowRelativeDate() {
      // console.log("previousMessageDate value:", this.previousMessageDate);
      if (!this.previousMessageDate) {
        return true; // Always show for the first message
      }
      const prevDate = new Date(this.previousMessageDate);
      return !this.isSameDay(this.messageDate, prevDate);
    },

    relativeDate() {
      const today = new Date();
      // console.log("message current time and date::::::", this.messageDate)
      if (this.isSameDay(today, this.messageDate)) {
        return 'Today';
      } else if (this.isYesterday(today, this.messageDate)) {
        return 'Yesterday';
        2;
      } else {
        return this.formatDate(this.messageDate);
      }
    },
    currentContact() {
      return this.currentChat.meta.sender;
    },
  },
  watch: {
    showDate(newValue) {
      console.log('showDate prop changed:', newValue);
    },
    data() {
      this.hasImageError = false;
      this.hasImgStoryError = false;
    },
  },
  mounted() {
    this.hasImageError = false;
    this.hasImgStoryError = false;
    bus.$on(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL, this.closeContextMenu);
    this.setupHighlightTimer();
  },
  beforeDestroy() {
    bus.$off(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL, this.closeContextMenu);
    clearTimeout(this.higlightTimeout);
  },
  methods: {
    isAttachmentImageVideoAudio(fileType) {
      return ['image', 'audio', 'video'].includes(fileType);
    },
    extractActiveStorageSignedId(url) {
      if (!url) return null;
      const match = String(url).match(
        /\/rails\/active_storage\/(?:blobs|representations)\/(?:redirect|proxy)\/([^/?#]+)\//
      );
      return match ? match[1] : null;
    },
    hasMediaAttachment(type) {
      if (this.hasAttachments && this.data.attachments.length > 0) {
        const { attachments = [{}] } = this.data;
        const { file_type: fileType } = attachments[0];
        return fileType === type && !this.hasImageError;
      }
      if (this.storyReply) {
        return true;
      }
      return false;
    },
    handleContextMenuClick() {
      this.showContextMenu = !this.showContextMenu;
    },
    async retrySendMessage() {
      await this.$store.dispatch('sendMessageWithData', this.data);
    },
    onImageLoadError() {
      this.hasImageError = true;
    },
    onStoryLoadError() {
      this.hasImgStoryError = true;
    },
    openContextMenu(e) {
      const shouldSkipContextMenu =
        e.target?.classList.contains('skip-context-menu') ||
        e.target?.tagName.toLowerCase() === 'a';
      if (shouldSkipContextMenu || getSelection().toString()) {
        return;
      }

      e.preventDefault();
      if (e.type === 'contextmenu') {
        this.$track(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
      }
      this.contextMenuPosition = {
        x: e.clientX,
        y: e.clientY + 25,
      };
      this.showContextMenu = true;
    },
    closeContextMenu() {
      this.showContextMenu = false;
      this.contextMenuPosition = { x: null, y: null };
    },
    setupHighlightTimer() {
      if (Number(this.$route.query.messageId) !== Number(this.data.id)) {
        return;
      }

      this.showBackgroundHighlight = true;
      const HIGHLIGHT_TIMER = 1000;
      this.higlightTimeout = setTimeout(() => {
        this.showBackgroundHighlight = false;
      }, HIGHLIGHT_TIMER);
    },
    isSameDay(date1, date2) {
      return (
        date1.getDate() === date2.getDate() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getFullYear() === date2.getFullYear()
      );
    },

    isYesterday(today, date) {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return this.isSameDay(yesterday, date);
    },

    formatDate(date) {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return date.toLocaleDateString(undefined, options);
    },
  },
};
</script>
<style lang="scss">
.color-blue-text {
  background-color: #40518a !important;
  color: #fff !important;
}

.wrap {
  @apply flex;

  > .bubble {
    @apply min-w-[70px];

    &.is-image,
    &.is-video {
      @apply p-0 overflow-hidden;

      .image,
      .video {
        @apply max-w-[20rem] p-0.5;

        > img,
        > video {
          @apply rounded-lg;
        }
        > video {
          @apply h-full w-full object-cover;
        }
      }
      .video {
        @apply h-[11.25rem];
      }
    }

    &.is-image.is-text > .message-text__wrap,
    &.is-video.is-text > .message-text__wrap {
      @apply max-w-[20rem] py-2 px-4;
    }

    &.is-private .file.message-text__wrap {
      .file--icon {
        @apply text-woot-400 dark:text-woot-400;
      }
      .text-block-title {
        @apply text-slate-700 dark:text-slate-700;
      }
      .download.button {
        @apply text-woot-400 dark:text-woot-400;
      }
    }

    &.is-private.is-text > .message-text__wrap .link {
      @apply text-woot-600 dark:text-woot-200;
    }
    &.is-private.is-text > .message-text__wrap .prosemirror-mention-node {
      @apply font-bold bg-none rounded-sm p-0 bg-yellow-100 dark:bg-yellow-700 text-slate-700 dark:text-slate-25 underline;
    }

    &.is-from-bot {
      @apply bg-violet-400 dark:bg-violet-400;
      background-color: #40518a !important;

      .message-text--metadata .time {
        @apply text-violet-50 dark:text-violet-50;
      }
      &.is-private .message-text--metadata .time {
        @apply text-slate-400 dark:text-slate-400;
      }
    }

    &.is-failed {
      @apply bg-red-200 dark:bg-red-200;

      .message-text--metadata .time {
        @apply text-red-50 dark:text-red-50;
      }
    }
  }

  &.is-pending {
    @apply relative opacity-80;

    .spinner {
      @apply absolute bottom-1 right-1;
    }

    > .is-image.is-text.bubble > .message-text__wrap {
      @apply p-0;
    }
  }
}

.wrap.is-email {
  --bubble-max-width: 84% !important;
}

.sender--info {
  @apply items-center text-black-700 dark:text-black-100 inline-flex py-1 px-0;

  .sender--available-name {
    @apply text-xs ml-1;
  }
}

.message-failed--alert {
  @apply text-red-900 dark:text-red-900 flex-grow text-right mt-1 mr-1 mb-0 ml-0;
}

li.left,
li.right {
  @apply flex items-end;
}

li.left.has-tweet-menu .context-menu {
  @apply mb-6;
}

li.has-bg {
  @apply bg-woot-75 dark:bg-woot-600;
}

li.left .wrap {
  position: relative;
}

li.left .context-menu-wrap--incoming {
  position: absolute;
  left: 100%;
  bottom: 0;
  margin-left: 16px;
}

li.left .message-block {
  flex: 0 0 auto;
  min-width: 0;
  max-width: 100%;
}

li.right .context-menu-wrap {
  @apply ml-auto;
  position: relative;
  top: -14px;
}

li.right {
  @apply flex-row-reverse justify-end;

  .wrap.is-pending {
    @apply ml-auto;
  }

  .wrap.is-failed {
    @apply flex items-end ml-auto;
  }
}

.has-context-menu {
  @apply bg-slate-50 dark:bg-slate-700;
}

.context-menu {
  @apply relative;
}

/* Markdown styling */

.bubble .text-content {
  p code {
    @apply bg-slate-75 dark:bg-slate-700 inline-block leading-none rounded-sm p-1;
  }

  ol li {
    @apply list-item list-decimal;
  }

  pre {
    @apply bg-slate-75 dark:bg-slate-700 block border-slate-75 dark:border-slate-700 text-slate-800 dark:text-slate-100 rounded-md p-2 mt-1 mb-2 leading-relaxed whitespace-pre-wrap;

    code {
      @apply bg-transparent text-slate-800 dark:text-slate-100 p-0;
    }
  }

  blockquote {
    @apply border-l-4 mx-0 my-1 pt-2 pr-2 pb-0 pl-4 border-slate-75 border-solid dark:border-slate-600 text-slate-800 dark:text-slate-100;

    p {
      @apply text-slate-800 dark:text-slate-300;
    }
  }
}

.right .bubble .text-content {
  p code {
    @apply bg-woot-600 dark:bg-woot-600 text-white dark:text-white;
  }

  pre {
    @apply bg-woot-800 dark:bg-woot-800 border-woot-700 dark:border-woot-700 text-white dark:text-white;

    code {
      @apply bg-transparent text-white dark:text-white;
    }
  }

  blockquote {
    @apply border-l-4 border-solid border-woot-400 dark:border-woot-400 text-white dark:text-white;

    p {
      @apply text-woot-75 dark:text-woot-75;
    }
  }
}

.story-reply-quote {
  @apply mt-2 mx-4 mb-0 px-2 pb-0 pt-2 border-l-4 border-solid border-slate-75 dark:border-slate-600 text-slate-600 dark:text-slate-200;
}
.left .wrap > div {
  // background-color: #fff !important;
  color: #000 !important;
}
.relative-date {
  position: absolute;
  top: -27px;
  left: 40%;
  font-size: 0.8rem;
  color: #30497b;
  border: 1px solid #c1b5b6;
  padding: 0px 18px;
  border-radius: 4px;
  background-color: white;
  font-weight: 600;
}
li.date-margin {
  margin-top: 26px;
}

.wrap > .bubble.is-campaign:not(.is-failed) {
  background-color: #9333ea !important;
  
  .message-text--metadata .time {
    color: #f3e8ff !important;
  }
  
  &.is-private .message-text--metadata .time {
    color: #94a3b8 !important;
  }
}
</style>